import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Close as CloseIcon, NavigateNext, NavigateBefore, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import OfferForm from './offerForm';
import MailDetailsView from '../mailDetailsView';
import { validateOfferAgainstSchema } from '../../schemas/offerCreationSchema';
import BatchEditForm, { BatchValues } from './batchEditForm';
import { MailOfferReviewStatus, OfferReview } from '../../../../models/mailOfferReview';

interface OfferReviewDialogProps {
  open: boolean;
  offers: OfferReview[];
  analyzing: boolean;
  mailDetails?: {
    from: string;
    subject: string;
    body: string;
  };
  mailStatus?: MailOfferReviewStatus;
  onClose: () => void;
  onCreate: () => void;
  onOffersChange: (offers: OfferReview[]) => void;
}

const OfferReviewDialog: React.FC<OfferReviewDialogProps> = ({
  open,
  offers,
  analyzing,
  mailDetails,
  mailStatus,
  onClose,
  onCreate,
  onOffersChange,
}) => {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(1); // Start on first offer (tab index 1)

  // Reset to summary tab when dialog opens
  useEffect(() => {
    if (open && offers.length > 0) {
      setCurrentTab(offers.length + 1); // Summary tab index
    }
  }, [open, offers.length]);

  // Tab indices: 0 = Mail Details, 1 to N = Offers, N+1 = Summary
  const summaryTabIndex = offers.length + 1;

  const handleOfferUpdate = (index: number, updatedOffer: OfferReview) => {
    const updatedOffers = [...offers];
    updatedOffers[index] = updatedOffer;
    onOffersChange(updatedOffers);
  };

  const handleDeleteOffer = (index: number) => {
    const updatedOffers = offers.filter((_, i) => i !== index);
    onOffersChange(updatedOffers);
    if (updatedOffers.length === 0) {
      setCurrentTab(0); // Go to mail details if no offers left
    } else if (currentTab > updatedOffers.length) {
      setCurrentTab(updatedOffers.length); // Go to last offer if we deleted the current one
    }
  };

  const handleBatchApply = (batchValues: BatchValues) => {
    const updatedOffers = offers.map(offer => {
      const updatedOffer = { ...offer };
      
      // Apply batch values only if they're defined
      if (batchValues.weightT !== undefined) updatedOffer.weightT = batchValues.weightT;
      if (batchValues.lengthM !== undefined) updatedOffer.lengthM = batchValues.lengthM;
      if (batchValues.fePrice !== undefined) updatedOffer.fePrice = batchValues.fePrice;
      
      // Apply loading place dates
      if (updatedOffer.loadingPlaces && updatedOffer.loadingPlaces[0]) {
        if (batchValues.loadingEarliestDate !== undefined) {
          updatedOffer.loadingPlaces[0].earliestLoadingDate = batchValues.loadingEarliestDate;
        }
        if (batchValues.loadingLatestDate !== undefined) {
          updatedOffer.loadingPlaces[0].latestLoadingDate = batchValues.loadingLatestDate;
        }
      }
      
      // Apply unloading place dates
      if (updatedOffer.loadingPlaces && updatedOffer.loadingPlaces[1]) {
        if (batchValues.unloadingEarliestDate !== undefined) {
          updatedOffer.loadingPlaces[1].earliestLoadingDate = batchValues.unloadingEarliestDate;
        }
        if (batchValues.unloadingLatestDate !== undefined) {
          updatedOffer.loadingPlaces[1].latestLoadingDate = batchValues.unloadingLatestDate;
        }
      }
      
      return updatedOffer;
    });
    
    onOffersChange(updatedOffers);
  };

  const handleNext = () => {
    if (currentTab < summaryTabIndex) {
      setCurrentTab(currentTab + 1);
    }
  };

  const handlePrevious = () => {
    if (currentTab > 0) {
      setCurrentTab(currentTab - 1);
    }
  };

  const isSummaryTab = currentTab === summaryTabIndex;
  const isFirstTab = currentTab === 0; // First tab is mail details

  const allOffersValid = offers.every(offer => validateOfferAgainstSchema(offer, t).length === 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{ '& .MuiDialog-paper': { height: '90vh', width: '95vw' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>{t('Review Extracted Offers')}</Typography>
        <Button variant="text" size="small" onClick={onClose} sx={{ minWidth: 'auto' }}>
          <CloseIcon />
        </Button>
      </DialogTitle>

      {offers.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
            <Tab label={t('Mail Details')} />
            {offers.map((_, index) => (
              <Tab key={index} label={`${t('Offer')} ${index + 1}`} />
            ))}
            <Tab label={t('Summary')} />
          </Tabs>
        </Box>
      )}

      <DialogContent sx={{ pt: 2 }}>
        {offers.length === 0 ? (
          <Alert severity="info">{t('No offers extracted. Please verify that this mail contains relevant offer information.')}</Alert>
        ) : (
          <Box>
            {currentTab === 0 && mailDetails ? (
              <MailDetailsView
                mail={{
                  from: mailDetails.from,
                  subject: mailDetails.subject,
                  body: mailDetails.body,
                  status: mailStatus,
                }}
                bodyMaxHeight={600}
              />
            ) : isSummaryTab ? (
              <Box>
                <BatchEditForm onApplyToAll={handleBatchApply} />
                <Box sx={{ height: 24 }} />
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'background.default' }}>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{t('Offer')}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{t('From')}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{t('To')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{t('Price')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{t('Distance (km)')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{t('Weight (t)')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{t('Length (m)')}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{t('Loading Earliest')}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{t('Loading Latest')}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{t('Unloading Earliest')}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{t('Unloading Latest')}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{t('Action')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {offers.map((offer, index) => {
                        const loading = offer?.loadingPlaces?.[0]?.address;
                        const unloading = offer?.loadingPlaces?.[1]?.address;
                        const loadingEarliestDate = offer?.loadingPlaces?.[0]?.earliestLoadingDate;
                        const loadingLatestDate = offer?.loadingPlaces?.[0]?.latestLoadingDate;
                        const unloadingEarliestDate = offer?.loadingPlaces?.[1]?.earliestLoadingDate;
                        const unloadingLatestDate = offer?.loadingPlaces?.[1]?.latestLoadingDate;
                        const errors = validateOfferAgainstSchema(offer, t);
                        const hasError = (field: string) => errors.some(err => err.includes(field));
                        const errorCellSx = (field: string, value: any) => {
                          const isError = hasError(field);
                          const isEmpty = !value || (typeof value === 'string' && value.trim() === '');
                          if (isError && isEmpty) {
                            return { color: 'error.main', fontWeight: 500 };
                          }
                          return { color: isError ? 'error.main' : 'inherit' };
                        };
                        return (
                          <TableRow key={index}>
                            <TableCell align="center">{index + 1}</TableCell>
                            <TableCell align="center" sx={errorCellSx('Loading Place', `${loading?.city || ''}, ${loading?.country || ''}`)}>
                              {`${loading?.city || ''}, ${loading?.country || ''}`}
                            </TableCell>
                            <TableCell align="center" sx={errorCellSx('Unloading Place', `${unloading?.city || ''}, ${unloading?.country || ''}`)}>
                              {`${unloading?.city || ''}, ${unloading?.country || ''}`}
                            </TableCell>
                            <TableCell align="right" sx={errorCellSx('Price', offer?.fePrice)}>
                              {hasError('Price') && !offer?.fePrice ? 'Missing' : (offer?.fePrice ?? '-')}
                            </TableCell>
                            <TableCell align="right" sx={{ color: !offer?.distanceKms ? 'error.main' : 'inherit', fontWeight: !offer?.distanceKms ? 500 : 400 }}>
                              {!offer?.distanceKms ? t('Geocoding Failed') : offer.distanceKms}
                            </TableCell>
                            <TableCell align="right" sx={errorCellSx('Weight', offer?.weightT)}>
                              {hasError('Weight') && !offer?.weightT ? 'Missing' : (offer?.weightT ?? '-')}
                            </TableCell>
                            <TableCell align="right" sx={errorCellSx('Length', offer?.lengthM)}>
                              {hasError('Length') && !offer?.lengthM ? 'Missing' : (offer?.lengthM ?? '-')}
                            </TableCell>
                            <TableCell align="center" sx={errorCellSx('Earliest Start', loadingEarliestDate)}>
                              {hasError('Earliest Start') && !loadingEarliestDate ? 'Missing' : (loadingEarliestDate ? new Date(loadingEarliestDate).toLocaleDateString() : '-')}
                            </TableCell>
                            <TableCell align="center" sx={errorCellSx('Latest Start', loadingLatestDate)}>
                              {hasError('Latest Start') && !loadingLatestDate ? 'Missing' : (loadingLatestDate ? new Date(loadingLatestDate).toLocaleDateString() : '-')}
                            </TableCell>
                            <TableCell align="center" sx={errorCellSx('Earliest End', unloadingEarliestDate)}>
                              {hasError('Earliest End') && !unloadingEarliestDate ? 'Missing' : (unloadingEarliestDate ? new Date(unloadingEarliestDate).toLocaleDateString() : '-')}
                            </TableCell>
                            <TableCell align="center" sx={errorCellSx('Latest End', unloadingLatestDate)}>
                              {hasError('Latest End') && !unloadingLatestDate ? 'Missing' : (unloadingLatestDate ? new Date(unloadingLatestDate).toLocaleDateString() : '-')}
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleDeleteOffer(index)}
                              >
                                {t('Delete')}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <OfferForm
                offer={offers[currentTab - 1]} // -1 because tab 0 is mail details
                onChange={(updatedOffer) => handleOfferUpdate(currentTab - 1, updatedOffer)}
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined">
          {t('Cancel')}
        </Button>
        
        {offers.length > 0 && (
          <>
            {!isFirstTab && !isSummaryTab && offers.length > 1 && (
              <Button
                onClick={() => handleDeleteOffer(currentTab - 1)}
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
              >
                {t('Delete Offer')}
              </Button>
            )}
            
            {!isFirstTab && (
              <Button
                onClick={handlePrevious}
                variant="outlined"
                startIcon={<NavigateBefore />}
              >
                {t('Previous')}
              </Button>
            )}
            
            {!isSummaryTab ? (
              <Button
                onClick={handleNext}
                variant="contained"
                endIcon={<NavigateNext />}
              >
                {t('Next')}
              </Button>
            ) : null}
            
            {isSummaryTab && (
              <Button
                onClick={onCreate}
                variant="contained"
                color="success"
                disabled={analyzing || !allOffersValid}
                startIcon={analyzing ? <CircularProgress size={20} /> : undefined}
                title={!allOffersValid ? t('Please fill all required fields in all offers') : undefined}
              >
                {t('Create Offers')}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OfferReviewDialog;
